import React, {FC, useEffect} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {CtaButton} from '@optimaxdev/design-system';

import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {VisiblyExam} from 'components/success/contacts/visiblyExam/visiblyExam';
import {TYP_CONTACT_LENSES} from 'constants/analytics/events/typ';
import {CL_RX_METHOD} from 'constants/success';
import {AppThunkDispatchType, ApplicationStoreType} from 'desktop/store';

import {useBindExam} from 'hooks/success/useBindExam';
import {getLabel} from 'libs/success/helpers/helpers';
import {validateVisiblyFields} from 'libs/success/prescriptionPopup/visibly';
import {getStatusesUpdatedItems} from 'libs/success/statuses/statuses';
import {VisiblyDetailsType} from 'types/successPage';

import {getDrawerState} from '../../../selectorsV2';
import {
    clearExamBind,
    fetchExamID,
    setVisibilyFailureMessage,
    setVisiblyFields,
} from '../../../store/actions/orderItems/orderItems';
import {setDrawerMethod, setDrawerState} from '../../../store/reducers/drawerPopup/drawerPopup';
import {
    setIsOrderStatusHasError,
    setOrderStatusLoadingState,
    updateItems,
} from '../../../store/reducers/orderStatus/orderStatus';
import {getSuccessPageItems, getVisiblyData} from '../../../store/selectors/orderItems/orderItems';

import {getOrderLoading} from '../../../store/selectors/orderStatus/orderStatus';
import {ScreenWrapper} from '../../contacts/screenWrapper/screenWrapper';

/**
 * Visibility exam component
 */
export const VisiblyExamWrapper: FC = () => {
    const {isLoading, item, items, visiblyData} = useSelector(
        (store: ApplicationStoreType) => ({
            isLoading: getOrderLoading(store),
            item: getDrawerState(store).item,
            items: getSuccessPageItems(store),
            visiblyData: getVisiblyData(store),
        }),
        shallowEqual,
    );

    const dispatch = useDispatch<AppThunkDispatchType>();

    const onBindSuccess = () => {
        dispatch(clearExamBind());
    };

    const onBindFailure = () => {
        sendDesktopAnalyticsEvent(
            TYP_CONTACT_LENSES,
            `${getLabel(CL_RX_METHOD.EYE_EXAM)} - Error - There was an error processing your vision test link`,
        );

        dispatch(setIsOrderStatusHasError(true));
    };

    useBindExam({
        examToken: visiblyData?.examToken || '',
        onFailure: onBindFailure,
        onSuccess: onBindSuccess,
    });

    useEffect(() => {
        dispatch(fetchExamID({itemId: Number(item?.id)}));
    }, []);

    const handleChange = (data: VisiblyDetailsType) => {
        dispatch(setVisiblyFields(data));
    };

    const handleSubmit = () => {
        const {errors, examID, values} = visiblyData;
        if (errors.dateOfBirth || errors.state || !examID) return;
        dispatch(setOrderStatusLoadingState(true));
        sendDesktopAnalyticsEvent(
            TYP_CONTACT_LENSES,
            getLabel(CL_RX_METHOD.EYE_EXAM_ELIGIBILITY, true),
        );

        // Open eye exam link here
        validateVisiblyFields({
            dateOfBirth: values.dateOfBirth,
            examID,
            onFailure: ({response}) => {
                const disqualifications = (
                    response?.data as {
                        disqualifications?: 'age' | 'region' | undefined;
                    }
                )?.disqualifications;
                dispatch(setOrderStatusLoadingState(false));
                dispatch(setVisibilyFailureMessage(disqualifications || 'age'));
                dispatch(setDrawerMethod({rxMethod: CL_RX_METHOD.EYE_EXAM_ELIGIBILITY_FAILED}));
            },
            onSuccess: ({url}) => {
                const updatedItems = getStatusesUpdatedItems(items);
                window.open(url, '_blank', 'noopener noreferrer');
                dispatch(updateItems(updatedItems));
                dispatch(setDrawerState(false));
                dispatch(setOrderStatusLoadingState(false));
            },
            state: values.state,
        });
    };

    return (
        <ScreenWrapper
            buttons={
                <CtaButton
                    colorName="secondary"
                    isDisabled={!visiblyData.values.dateOfBirth || !visiblyData.values.state}
                    isLoading={isLoading}
                    onClick={handleSubmit}
                >
                    Continue
                </CtaButton>
            }
        >
            <VisiblyExam setVisiblyFields={handleChange} visiblyData={visiblyData} />
        </ScreenWrapper>
    );
};
